import {
    Box, Typography, Button, Modal, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Skeleton, Avatar, Select,
    MenuItem, FormControl, InputLabel, ToggleButton, ToggleButtonGroup,
    IconButton, Tooltip, Alert
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { Menu as PrimeMenu } from 'primereact/menu';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizSharpIcon from '@mui/icons-material/MoreHorizSharp';
import CloseIcon from '@mui/icons-material/Close';
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const CATEGORIES = [
    { value: 'uniform_hygiene', label: 'Uniform & Hygiene' },
    { value: 'discipline', label: 'Discipline' },
];

const SEVERITIES = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
];

const CATEGORY_STYLE = {
    uniform_hygiene: { bg: '#FFF7E6', border: '#FA8C16', color: '#FA8C16' },
    discipline: { bg: '#FFF1F0', border: '#F5222D', color: '#F5222D' },
};

const SEVERITY_STYLE = {
    low: { bg: '#FAFAFA', border: '#BFBFBF', color: '#6B7280' },
    medium: { bg: '#FFF7E6', border: '#FA8C16', color: '#FA8C16' },
    high: { bg: '#FFF1F0', border: '#F5222D', color: '#F5222D' },
    critical: { bg: '#FFF1F0', border: '#F5222D', color: '#F5222D' },
};

const STATUS_STYLE = {
    open: { bg: '#FAFAFA', border: '#BFBFBF', color: '#6B7280' },
    action_taken: { bg: '#E6F4FF', border: '#0071BC', color: '#0071BC' },
    closed: { bg: '#F6FFED', border: '#52C41A', color: '#52C41A' },
};

const EMPTY_FORM = {
    staff_id: '',
    category: 'uniform_hygiene',
    severity: 'low',
    incident_date: dayjs().format('YYYY-MM-DD'),
    description: '',
    action_taken: '',
};

function Chip({ style, label }) {
    return (
        <Box sx={{
            display: 'inline-flex', justifyContent: 'center',
            backgroundColor: style.bg, border: '1px solid', borderColor: style.border,
            borderRadius: '2px', padding: '1px 8px', whiteSpace: 'nowrap'
        }}>
            <Typography sx={{ fontSize: '11px', lineHeight: '20px', color: style.color, textTransform: 'uppercase' }}>
                {label}
            </Typography>
        </Box>
    );
}

function StatCard({ label, value, hint, accent }) {
    return (
        <Box sx={{
            flex: 1, minWidth: '150px', backgroundColor: 'background.light',
            borderRadius: '12px', padding: '13px 16px',
            boxShadow: '0px 0px 25px 0px #00000012',
            borderLeft: '3px solid', borderColor: accent
        }}>
            <Typography sx={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.07em', color: 'text.secondary' }}>{label}</Typography>
            <Typography sx={{ fontSize: '22px', fontWeight: 600, lineHeight: 1.25 }}>{value}</Typography>
            <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>{hint}</Typography>
        </Box>
    );
}

function initialsOf(name) {
    if (!name) return '--';
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

export function StaffCompliance() {
    const selectedHub = useSelector(state => state.user.selected_hub);
    const toast = useRef(null);
    const menuRefs = useRef([]);
    const fileInputRef = useRef(null);

    const [incidents, setIncidents] = useState([]);
    const [scope, setScope] = useState('all');
    const [staffOptions, setStaffOptions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [categoryFilter, setCategoryFilter] = useState('');
    const [severityFilter, setSeverityFilter] = useState('');
    const [staffFilter, setStaffFilter] = useState('');
    const [range, setRange] = useState([dayjs().subtract(1, 'month').toDate(), new Date()]);

    const [modalOpen, setModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [pendingPhotos, setPendingPhotos] = useState([]);
    const [existingPhotos, setExistingPhotos] = useState([]);

    const [viewer, setViewer] = useState(null);

    const hubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };
        const fromStore = normalize(selectedHub);
        if (fromStore) return fromStore;
        const stored = localStorage.getItem('selected_hub');
        if (!stored) return null;
        try {
            return normalize(JSON.parse(stored));
        } catch (e) {
            return normalize(stored);
        }
    }, [selectedHub]);

    const showToast = (severity, summary, detail) => {
        if (toast.current) toast.current.show({ severity, summary, detail, life: 4000 });
    };

    const errorMessage = (error, fallback) => {
        const detail = error?.response?.data?.error;
        if (!detail) return fallback;
        if (typeof detail === 'string') return detail;
        const firstKey = Object.keys(detail)[0];
        const firstValue = detail[firstKey];
        return Array.isArray(firstValue) ? firstValue[0] : String(firstValue);
    };

    useEffect(() => {
        if (!hubId) return;
        fetchStaff();
    }, [hubId]);

    useEffect(() => {
        if (!hubId) return;
        fetchIncidents();
        fetchStats();
    }, [hubId, categoryFilter, severityFilter, staffFilter, range]);

    const filterParams = () => {
        const params = { hub_id: hubId };
        if (categoryFilter) params.category = categoryFilter;
        if (severityFilter) params.severity = severityFilter;
        if (staffFilter) params.staff_id = staffFilter;
        if (Array.isArray(range) && range[0]) {
            params.from = dayjs(range[0]).format('YYYY-MM-DD');
            params.to = dayjs(range[1] || range[0]).format('YYYY-MM-DD');
        }
        return params;
    };

    const fetchStaff = async () => {
        try {
            const res = await CustomAxios.get('staff/options/', { params: { hub_id: hubId } });
            setStaffOptions(res.data || []);
        } catch (error) {
            setStaffOptions([]);
        }
    };

    const fetchIncidents = async () => {
        try {
            setLoading(true);
            const res = await CustomAxios.get('staff/compliance/', { params: filterParams() });
            setIncidents(res.data.results || []);
            setScope(res.data.scope || 'all');
            menuRefs.current = (res.data.results || []).map((_, i) => menuRefs.current[i] || React.createRef());
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load incidents'));
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await CustomAxios.get('staff/compliance/stats/', { params: filterParams() });
            setStats(res.data);
        } catch (error) {
            setStats(null);
        }
    };

    const openModal = (item = null) => {
        if (item) {
            setEditId(item.id);
            setFormData({
                staff_id: item.staff,
                category: item.category,
                severity: item.severity,
                incident_date: item.incident_date,
                description: item.description || '',
                action_taken: item.action_taken || '',
            });
            setExistingPhotos(item.photos || []);
        } else {
            setEditId(null);
            setFormData(EMPTY_FORM);
            setExistingPhotos([]);
        }
        setPendingPhotos([]);
        setModalOpen(true);
    };

    const handleFiles = (event) => {
        const files = Array.from(event.target.files || []);
        const room = 5 - existingPhotos.length - pendingPhotos.length;
        if (files.length > room) {
            showToast('warn', 'Too many photos', `An incident can hold at most 5 photos; ${room} slot(s) left.`);
        }
        const oversize = files.find(f => f.size > 5 * 1024 * 1024);
        if (oversize) {
            showToast('error', 'File too large', `${oversize.name} is larger than 5 MB.`);
        }
        setPendingPhotos(prev => [
            ...prev,
            ...files.filter(f => f.size <= 5 * 1024 * 1024).slice(0, Math.max(room, 0)),
        ]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async () => {
        if (!formData.staff_id) {
            showToast('error', 'Validation Error', 'Pick a staff member');
            return;
        }
        if (!formData.description.trim()) {
            showToast('error', 'Validation Error', 'A description is required');
            return;
        }

        // Multipart because photos ride along; CustomAxios drops the JSON
        // content-type for FormData so the browser sets the boundary.
        const body = new FormData();
        body.append('staff_id', formData.staff_id);
        body.append('category', formData.category);
        body.append('severity', formData.severity);
        body.append('incident_date', formData.incident_date);
        body.append('description', formData.description);
        body.append('action_taken', formData.action_taken || '');
        pendingPhotos.forEach(file => body.append('photos', file));

        try {
            setSaving(true);
            if (editId) {
                await CustomAxios.put(`staff/compliance/${editId}/`, body);
            } else {
                await CustomAxios.post('staff/compliance/', body);
            }
            showToast('success', 'Saved', editId ? 'Incident updated' : 'Incident logged');
            setModalOpen(false);
            fetchIncidents();
            fetchStats();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not save the incident'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(
            `Delete this incident against ${item.staff_name}? This removes it from their file permanently.`
        )) return;
        try {
            await CustomAxios.delete(`staff/compliance/${item.id}/`);
            showToast('success', 'Deleted', 'Incident removed');
            fetchIncidents();
            fetchStats();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Delete failed'));
        }
    };

    const handleClose = async (item) => {
        try {
            await CustomAxios.put(`staff/compliance/${item.id}/`, { status: 'closed' });
            showToast('success', 'Closed', 'Incident marked closed');
            fetchIncidents();
            fetchStats();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not close the incident'));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Typography variant="h5">Compliance Log</Typography>
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: '160px' }}>
                        <InputLabel>Category</InputLabel>
                        <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: '120px' }}>
                        <InputLabel>Severity</InputLabel>
                        <Select value={severityFilter} label="Severity" onChange={(e) => setSeverityFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {SEVERITIES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: '170px' }}>
                        <InputLabel>Staff</InputLabel>
                        <Select value={staffFilter} label="Staff" onChange={(e) => setStaffFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {staffOptions.map(s => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box sx={{ width: '230px' }}>
                        <Calendar value={range} onChange={(e) => setRange(e.value)} selectionMode="range"
                            readOnlyInput hideOnRangeSelection showIcon dateFormat="dd-mm-yy" style={{ width: '100%' }} />
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openModal()}>
                        Log Incident
                    </Button>
                </Box>
            </Box>

            {scope === 'own' && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '12.5px' }}>
                    You are seeing only the incidents you raised. The full compliance history for this
                    hub is visible to HR admins.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', mb: 3 }}>
                <StatCard label="Incidents" value={stats?.total ?? '—'} hint="in range" accent="primary.main" />
                <StatCard label="Uniform & hygiene" value={stats?.uniform_hygiene ?? '—'} hint="category" accent="#FA8C16" />
                <StatCard label="Discipline" value={stats?.discipline ?? '—'} hint="category" accent="borders.red" />
                <StatCard label="High / critical" value={stats?.high_severity ?? '—'} hint="need attention" accent="borders.red" />
                <StatCard label="Open" value={stats?.open ?? '—'} hint="no action recorded" accent="secondary.border" />
                <StatCard label="Closed" value={stats?.closed ?? '—'} hint="resolved" accent="borders.green" />
            </Box>

            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Incident log</Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        newest first · photo links expire after 6 hours
                    </Typography>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Date', 'Staff', 'Category', 'Severity', 'Description', 'Photos', 'Raised by', 'Status', ''].map((h, i) => (
                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', whiteSpace: 'nowrap' }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={9} sx={{ backgroundColor: 'white', height: '180px' }}>
                                {[0, 1, 2].map(i => <Skeleton key={i} animation="wave" sx={{ height: '44px', background: 'rgb(0 0 0 / 6%)' }} />)}
                            </TableCell></TableRow>
                        ) : incidents.length ? incidents.map((item, ind) => (
                            <TableRow key={item.id}>
                                <TableCell sx={{ backgroundColor: 'white', whiteSpace: 'nowrap', fontSize: '12px' }}>
                                    {dayjs(item.incident_date).format('DD MMM YYYY')}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Avatar sx={{ width: 26, height: 26, fontSize: '10px', bgcolor: 'background.table', color: 'button.blue' }}>
                                            {initialsOf(item.staff_name)}
                                        </Avatar>
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.staff_name}</Typography>
                                            <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>{item.employee_id}</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <Chip style={CATEGORY_STYLE[item.category]} label={item.category === 'uniform_hygiene' ? 'Uniform' : 'Discipline'} />
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <Chip style={SEVERITY_STYLE[item.severity]} label={item.severity_label} />
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white', fontSize: '12px', maxWidth: '260px' }}>
                                    {item.description}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    {item.photo_count ? (
                                        <Box sx={{ display: 'flex', gap: '4px' }}>
                                            {item.photos.map((url, i) => (
                                                <Box
                                                    key={i} component="img" src={url}
                                                    onClick={() => setViewer(url)}
                                                    sx={{
                                                        width: 30, height: 30, borderRadius: '4px',
                                                        objectFit: 'cover', cursor: 'pointer',
                                                        border: '1px solid', borderColor: 'secondary.border'
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>—</Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white', fontSize: '12px' }}>{item.raised_by_name || '—'}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <Chip style={STATUS_STYLE[item.status]} label={item.status_label} />
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <PrimeMenu
                                        model={[
                                            { label: 'Edit / record action', command: () => openModal(item) },
                                            ...(item.status !== 'closed'
                                                ? [{ label: 'Mark closed', command: () => handleClose(item) }]
                                                : []),
                                            { label: 'Delete', command: () => handleDelete(item) },
                                        ]}
                                        popup ref={menuRefs.current[ind]} className="custom-menu"
                                        style={{ marginTop: '-10px' }}
                                    />
                                    <Tooltip title="Actions" arrow>
                                        <IconButton onClick={(e) => menuRefs.current[ind]?.current?.toggle(e)}>
                                            <MoreHorizSharpIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', backgroundColor: 'white', height: '180px', fontWeight: 700 }}>
                                No incidents in this range
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '92%', sm: 500 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 4,
                    borderRadius: 2, maxHeight: '90vh', overflow: 'auto'
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {editId ? 'Edit Incident' : 'Log Incident'}
                    </Typography>

                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.06em', mb: 1 }}>
                        Category
                    </Typography>
                    <ToggleButtonGroup
                        exclusive size="small" value={formData.category}
                        onChange={(e, value) => value && setFormData({ ...formData, category: value })}
                        sx={{ mb: 2 }}
                    >
                        {CATEGORIES.map(c => (
                            <ToggleButton key={c.value} value={c.value} sx={{ px: 2, fontSize: '11px' }}>{c.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <FormControl fullWidth required sx={{ mb: 2 }} disabled={Boolean(editId)}>
                        <InputLabel>Staff</InputLabel>
                        <Select value={formData.staff_id} label="Staff"
                            onChange={(e) => setFormData({ ...formData, staff_id: e.target.value })}>
                            {staffOptions.map(s => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField fullWidth label="Incident date" type="date" value={formData.incident_date}
                            onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                            InputLabelProps={{ shrink: true }} />
                        <FormControl fullWidth>
                            <InputLabel>Severity</InputLabel>
                            <Select value={formData.severity} label="Severity"
                                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}>
                                {SEVERITIES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>

                    <TextField fullWidth required multiline rows={3} label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        sx={{ mb: 2 }} />

                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.06em', mb: 1 }}>
                        Photo evidence
                    </Typography>
                    <Box
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            border: '1px dashed', borderColor: 'secondary.border', borderRadius: '6px',
                            padding: '14px', textAlign: 'center', cursor: 'pointer',
                            backgroundColor: 'background.upload', mb: 1
                        }}
                    >
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                            Click to upload
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                            JPG / PNG · max 5 MB · up to 5 photos · stored privately
                        </Typography>
                    </Box>
                    <input ref={fileInputRef} type="file" accept="image/*" multiple
                        onChange={handleFiles} style={{ display: 'none' }} />

                    {(existingPhotos.length > 0 || pendingPhotos.length > 0) && (
                        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mb: 2 }}>
                            {existingPhotos.map((url, i) => (
                                <Box key={`e-${i}`} component="img" src={url} onClick={() => setViewer(url)}
                                    sx={{ width: 56, height: 56, borderRadius: '6px', objectFit: 'cover', cursor: 'pointer', border: '1px solid', borderColor: 'secondary.border' }} />
                            ))}
                            {pendingPhotos.map((file, i) => (
                                <Box key={`p-${i}`} sx={{ position: 'relative' }}>
                                    <Box component="img" src={URL.createObjectURL(file)}
                                        sx={{ width: 56, height: 56, borderRadius: '6px', objectFit: 'cover', border: '1px solid', borderColor: 'primary.main' }} />
                                    <IconButton
                                        size="small"
                                        onClick={() => setPendingPhotos(prev => prev.filter((_, idx) => idx !== i))}
                                        sx={{
                                            position: 'absolute', top: -8, right: -8, backgroundColor: 'white',
                                            border: '1px solid', borderColor: 'secondary.border', padding: '2px'
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 12 }} />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <TextField fullWidth multiline rows={2} label="Action taken"
                        placeholder="Optional — can be filled later"
                        helperText="Leaving this empty keeps the incident Open."
                        value={formData.action_taken}
                        onChange={(e) => setFormData({ ...formData, action_taken: e.target.value })}
                        sx={{ mb: 2 }} />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                            {saving ? 'Saving…' : (editId ? 'Update' : 'Save')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={Boolean(viewer)} onClose={() => setViewer(null)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', maxWidth: '90vw', maxHeight: '90vh',
                    outline: 'none'
                }}>
                    <Box component="img" src={viewer || ''} sx={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px' }} />
                </Box>
            </Modal>
        </Box>
    );
}
